<template>
  <rest-page ref="restPage" :fetch-endpoint="endpoints">
    <page-header
      :badge="badge"
      :breadcrumbs="breadcrumbs"
      :title="applicationName"
    >
      <template slot="actions">
        <el-button
          v-if="isActivated"
          icon="el-icon-copy-document"
          plain
          type="primary"
          @click="copyToClipboard(model.appkey, 'key')"
        >
          {{ $t('ApplicationsChannels_CopySDKKey') }}
        </el-button>
        <el-button
          v-if="isActivated"
          icon="el-icon-copy-document"
          plain
          type="primary"
          @click="copyToClipboard(model.webpushCode, 'code')"
        >
          {{ $t('ApplicationsChannels_CopySDKCode') }}
        </el-button>
        <el-button
          icon="el-icon-setting"
          plain
          type="primary"
          @click="formDialogVisible = true"
        >
          {{ $t('ApplicationsChannels_Settings') }}
        </el-button>
      </template>
    </page-header>
    <page-content>
      <get-started-block
        v-if="!isActivated"
        v-model="model"
        @reload="reloadModel"
        @copy-key="copyToClipboard"
      />
      <channels
        v-model="model"
        :channels="channels"
        @reload="reloadModel"
        @switch-channel="switchChannel"
      ></channels>
      <el-dialog
        :before-close="handleFormDialogClose"
        :close-on-click-modal="false"
        :modal-append-to-body="false"
        :top="popupTopMargin"
        :visible="formDialogVisible"
        class="form-dialog"
        width="650px"
      >
        <div slot="title">
          <div class="el-dialog__title">
            <div v-if="activeType === ApplicationTypes.ios">
              {{ $t('ApplicationList_Popup_Update_Add_IOS_Title') }}
            </div>
            <div v-if="activeType === ApplicationTypes.android">
              <span v-if="activeSubType === ApplicationSubTypes.huawei">
                {{
                  $t('ApplicationList_Popup_Update_Add_Android_Huawei_Title')
                }}
              </span>
              <span v-else>
                {{ $t('ApplicationList_Popup_Update_Add_Android_Title') }}
              </span>
            </div>
            <div v-if="activeType === ApplicationTypes.web">
              {{ $t('ApplicationList_Popup_Update_Add_Website_Title') }}
            </div>
          </div>
        </div>
        <app-form
          v-if="activeType"
          ref="app-form"
          :type="activeType"
          :sub-type="activeSubType"
          @keyup.native="change"
        />
        <span slot="footer" class="dialog-footer">
          <el-button plain type="primary" @click="handleFormDialogClose">
            <span>{{ $t('ApplicationList_Button_Back') }}</span>
          </el-button>
          <el-button :disabled="!isFormValid" type="primary" @click="save">
            <i class="el-icon-check el-icon--left" />
            <span>
              {{ $t('ApplicationList_Button_Update') }}
            </span>
          </el-button>
        </span>
      </el-dialog>
    </page-content>
  </rest-page>
</template>

<script>
import { customConfirm, projectEndpoint, projectRoute } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import RestPage from '@/common/components/rest/RestPage'
import PageHeader from '@/common/components/page/PageHeaderNew'
import PageContent from '@/common/components/page/PageContent'
import GetStartedBlock from '@/modules/account/components/GetStartedBlock'
import Channels from '@/modules/account/components/Channels'
import AppForm from '@/modules/account/components/AppForm'
import {
  ApplicationSubTypes,
  ApplicationTypes
} from '@/modules/account/enum/ApplicationEnum'
import { EventBus } from '@/core/EventBus'

export default {
  name: 'ApplicationChannelsPage',

  components: {
    PageContent,
    RestPage,
    PageHeader,
    GetStartedBlock,
    Channels,
    AppForm
  },

  data() {
    return {
      model: this.defaultModel(),
      formDialogVisible: false,
      isFormValid: true,
      ApplicationTypes,
      ApplicationSubTypes,
      channels: []
    }
  },

  computed: {
    applicationName() {
      if (this.model && this.model.name) {
        return this.model.name
      }

      return this.$t('ApplicationsChannels_ActivationOk')
    },
    activeType() {
      if (this.model && this.model.type) {
        return this.model.type
      }
      return null
    },
    activeSubType() {
      if (
        this.model &&
        this.model.type === 'android' &&
        this.model.huawei_app_id
      ) {
        return ApplicationSubTypes.huawei
      }
      return null
    },
    endpoints() {
      return [this.applicationEndpoint, this.channelsEndpoint]
    },
    applicationEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `applications/${this.$route.query.id}`,
        params: {
          expand: 'webpushCode,inbox_settings,push_setup'
        },
        handler: data => {
          this.model = data
        }
      })
    },
    channelsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: ``,
        params: {
          expand: 'channels'
        },
        handler: data => {
          this.channels = data.channels
        }
      })
    },

    channelSwitchEndpoint() {
      return projectEndpoint({
        method: 'patch',
        url: `applications/${this.$route.query.id}`
      })
    },

    breadcrumbs() {
      return [
        {
          label: this.$t('ApplicationList_Title'),
          route: projectRoute({
            name: 'Applications'
          })
        },
        {
          label: this.applicationName
        }
      ]
    },

    isActivated() {
      if (!this.model) return false

      return !!this.model.activated
    },

    badge() {
      if (!this.model || !this.isActivated) return null

      return {
        class: 'ApplicationStatusBadge'
      }
    },

    popupTopMargin() {
      if (
        window.innerHeight > 700 ||
        this.activeType !== ApplicationTypes.web
      ) {
        return '15vh'
      }

      return '5vh'
    }
  },

  methods: {
    change() {
      this.$refs['app-form'].$refs['form']
        .validate()
        .then(res => {
          if (res === true) {
            this.isFormValid = true
          }
        })
        .catch(() => {
          this.isFormValid = false
        })
    },
    save() {
      this.$refs['app-form'].$refs['form'].save()
    },
    defaultModel() {
      if (this.$route.query.id) {
        return null
      } else {
        return {
          type: this.$route.query.type,
          project_id: this.$route.query.project_id
        }
      }
    },
    handleFormDialogClose() {
      this.formDialogVisible = false
    },
    reloadModel() {
      RestApi({
        ...this.applicationEndpoint
      })
        .then(({ data }) => {
          this.model = data
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    copyToClipboard(value, type) {
      navigator.clipboard
        .writeText(value)
        .then(_ =>
          this.$message({
            message: this.$t(
              type === 'key'
                ? 'ApplicationsChannels_KeyCopiedSuccessfully'
                : 'ApplicationsChannels_CodeCopiedSuccessfully'
            ),
            type: 'success'
          })
        )
        .catch(_ =>
          this.$message.error(
            this.$t('ApplicationsChannels_KeyCopiedUnsuccessfully')
          )
        )
    },

    switchChannel(payload) {
      if (payload.value === 0) {
        this.showPopup(payload)
      } else {
        this.switchChannelOriginal(payload)
      }
    },

    showPopup(payload) {
      customConfirm(
        this.$t('ApplicationsChannels_DisableTitle'),
        this.$t('ApplicationsChannels_DisableMessage'),
        this.$t('ApplicationsChannels_Disable'),
        undefined,
        undefined,
        ' custom-confirm-ce'
      ).then(_ => this.switchChannelOriginal(payload))
    },

    switchChannelOriginal(payload) {
      const data = {
        ...this.model
      }
      data[payload.channel] = payload.value

      RestApi({
        ...this.channelSwitchEndpoint,
        data: data
      })
        .then(_ => {
          this.$message({
            message:
              payload.value === 0
                ? this.$t('ApplicationsChannels_ChannelDisabled')
                : this.$t('ApplicationsChannels_ChannelEnabled'),
            type: 'success'
          })
          this.$store.dispatch('refreshPlatformChannels')
          this.$store.dispatch('reloadProjectConfig')
          this.reloadModel()
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
          this.$store.dispatch('refreshPlatformChannels')
          this.$store.dispatch('reloadProjectConfig')
          this.reloadModel()
        })
    }
  },
  mounted() {
    EventBus.$on('closeAppDialogs', () => {
      this.reloadModel()
      this.handleFormDialogClose()
    })
  }
}
</script>

<style scoped>
>>> .ApplicationStatusBadge {
  margin-left: 12px;
  margin-top: 7px;
}

>>> .form-dialog .el-dialog__body {
  padding: 2% 7% 2% 7%;
  max-height: 65vh;
  overflow-x: hidden;
  overflow-y: auto;
}
</style>



// WEBPACK FOOTER //
// src/modules/account/pages/ApplicationChannelsPage.vue